import { Clickable } from '@carvana/merch-components';
import cx from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import COOKIES from '~/constants/cookies';
import { commonEnvironment } from '~/constants/environment';
import { useClientSide } from '~/hooks/common';
import BrowserCookies from '~/utilities/BrowserCookies';
import Prompt from './Prompt';

type AppName = 'homepage' | 'merchui';

const didSetNativeAppCapabilities = 'vdp-set-app-capabilities';

function getEnabledCookieName(appName: AppName) {
  return `carvana-merch-${appName}`;
}

function getBranchCookieName(appName: AppName) {
  return `carvana-merch-${appName}-feature-name`;
}

function clearFeatureCookies(appName: AppName): () => void {
  return () => {
    BrowserCookies.remove(getEnabledCookieName(appName));
    BrowserCookies.remove(getBranchCookieName(appName));
    BrowserCookies.remove(COOKIES.nativeAppCapabilities);
    BrowserCookies.remove(didSetNativeAppCapabilities);

    window.location.reload();
  };
}

function useSetFeatureBranch(
  appName: AppName,
  setErrorMessage: (errorMessage: string) => void
): (branchName: string) => boolean {
  return useCallback(
    (branchName: string): boolean => {
      if (!branchName) {
        return false;
      }

      const featureBranchName = branchName
        .toLowerCase()
        .match(/reo-[0-9a-z-]*/)?.[0];

      if (featureBranchName) {
        BrowserCookies.set(getEnabledCookieName(appName), 'true');
        BrowserCookies.set(
          getBranchCookieName(appName),
          `carvana-merch-${appName}-${featureBranchName}`
        );

        return true;
      } else {
        setErrorMessage(
          `Could not parse a feature branch name from ${branchName}.`
        );

        return false;
      }
    },
    [appName, setErrorMessage]
  );
}

function useSetNativeAppCapabilities() {
  return useCallback((capabilities: string[]) => {
    if (capabilities.length === 0) return false;

    BrowserCookies.set(COOKIES.nativeAppCapabilities, capabilities.join(','));
    BrowserCookies.set(didSetNativeAppCapabilities, 'true');

    return true;
  }, []);
}

type OwnProps = {
  appName: AppName;
  className?: string;
};

export default function FeatureBranch({ appName, className }: OwnProps) {
  const [errorMessage, setErrorMessage] = useState('');
  const [showPrompt, setShowPrompt] = useState(false);
  const [showBanner, setShowBanner] = useState(true);

  const isClientSide = useClientSide();
  const setFeatureBranch = useSetFeatureBranch(appName, setErrorMessage);
  const setNativeAppCapabilities = useSetNativeAppCapabilities();

  const isFeatureBranchEnabled =
    BrowserCookies.get(getEnabledCookieName(appName)) === 'true';
  const branchName = BrowserCookies.get(getBranchCookieName(appName));

  const currentNativeAppCapabilities = BrowserCookies.get(
    COOKIES.nativeAppCapabilities
  );
  const isNativeAppCapabilitiesEnabled =
    Boolean(currentNativeAppCapabilities) &&
    BrowserCookies.get(didSetNativeAppCapabilities);
  const isEnabled = isFeatureBranchEnabled || isNativeAppCapabilitiesEnabled;

  useEffect(() => {
    setErrorMessage('');
  }, [showPrompt]);

  return isClientSide && commonEnvironment === 'test' ? (
    <>
      <div
        className={cx(
          'pointer-events-none absolute inset-0 z-50 mx-auto -:max-w-[1800px]',
          className
        )}
      >
        {isEnabled ? (
          <div className="m-4 flex w-fit max-w-full items-center bg-yellow-2 p-4 opacity-90">
            <Clickable
              element="span"
              onClick={() => {
                setShowBanner(v => !v);
              }}
              className="pointer-events-auto px-8 text-2xl leading-none"
              data-analytics-id="Toggle feature branch banner"
              title="Toggle feature branch banner"
            >
              {showBanner ? '–' : '+'}
            </Clickable>

            {showBanner ? (
              <>
                <div>
                  <div className="flex-1">
                    {branchName ? (
                      <>
                        <p className="m:inline">Using feature branch </p>
                        <p className="font-mono text-xs m:inline">
                          {branchName}
                        </p>
                      </>
                    ) : null}
                  </div>
                  <div className="flex-1">
                    {currentNativeAppCapabilities ? (
                      <>
                        <p className="m:inline">Using app capabilities </p>
                        <p className="font-mono text-xs m:inline">
                          {currentNativeAppCapabilities}
                        </p>
                      </>
                    ) : null}
                  </div>
                </div>

                <Clickable
                  element="span"
                  onClick={clearFeatureCookies(appName)}
                  className="pointer-events-auto px-8 text-2xl leading-none"
                  data-analytics-id="Clear feature branch"
                  title="Clear feature branch"
                >
                  ×
                </Clickable>
              </>
            ) : null}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setShowPrompt(true);
            }}
            className="pointer-events-auto opacity-0"
            data-analytics-id="Set feature branch"
            title="Set feature branch"
          >
            Set feature branch
          </button>
        )}
      </div>
      {showPrompt && (
        <Prompt
          errorMessage={errorMessage}
          onCancel={() => {
            setShowPrompt(false);
          }}
          onSave={(newBranchName: string, nativeAppCapabilities: string[]) => {
            const featureBranchSet = setFeatureBranch(newBranchName);
            const nativeAppCapabilitiesSet = setNativeAppCapabilities(
              nativeAppCapabilities
            );

            if (featureBranchSet || nativeAppCapabilitiesSet) {
              window.location.reload();
            } else {
              setErrorMessage('Branch name or app capabilities must be set.');
            }
          }}
        />
      )}
    </>
  ) : null;
}
